import { useMediaQuery } from '@mantine/hooks';

import {
  BREAKPOINTS,
  BREAKPOINT_SIZES,
  type BreakpointSize,
} from './useResponsive.constants';

import type {
  BreakpointBooleans,
  ResponsiveResult,
} from './useResponsive.types';

function useBreakpointMatches(
  feature: 'min-width' | 'max-width',
): BreakpointBooleans {
  // Default values match klue-ui behavior: min-width defaults to false,
  // max-width defaults to true (assumes a narrow viewport when unknown)
  const initialValue = feature === 'max-width';

  const xxsmall =
    useMediaQuery(`(${feature}: ${BREAKPOINTS.xxsmall})`, initialValue) ??
    initialValue;
  const xsmall =
    useMediaQuery(`(${feature}: ${BREAKPOINTS.xsmall})`, initialValue) ??
    initialValue;
  const small =
    useMediaQuery(`(${feature}: ${BREAKPOINTS.small})`, initialValue) ??
    initialValue;
  const medium =
    useMediaQuery(`(${feature}: ${BREAKPOINTS.medium})`, initialValue) ??
    initialValue;
  const large =
    useMediaQuery(`(${feature}: ${BREAKPOINTS.large})`, initialValue) ??
    initialValue;
  const xlarge =
    useMediaQuery(`(${feature}: ${BREAKPOINTS.xlarge})`, initialValue) ??
    initialValue;

  return { xxsmall, xsmall, small, medium, large, xlarge };
}

function getLowerBreakpoint(minWidth: BreakpointBooleans): BreakpointSize | '' {
  // The lower breakpoint is the largest breakpoint that matches min-width
  for (let i = BREAKPOINT_SIZES.length - 1; i >= 0; i--) {
    if (minWidth[BREAKPOINT_SIZES[i]]) {
      return BREAKPOINT_SIZES[i];
    }
  }
  return '';
}

function getUpperBreakpoint(maxWidth: BreakpointBooleans): BreakpointSize | '' {
  // The upper breakpoint is the smallest breakpoint that matches max-width
  for (let i = 0; i < BREAKPOINT_SIZES.length; i++) {
    if (maxWidth[BREAKPOINT_SIZES[i]]) {
      return BREAKPOINT_SIZES[i];
    }
  }
  return '';
}

export function useResponsive(): ResponsiveResult {
  const minWidth = useBreakpointMatches('min-width');
  const maxWidth = useBreakpointMatches('max-width');

  return {
    minWidth,
    maxWidth,
    lowerBreakpoint: getLowerBreakpoint(minWidth),
    upperBreakpoint: getUpperBreakpoint(maxWidth),
  };
}
